import {
  ConstructionCategory,
  type ConstructionCategoryValue,
  type ConstructionDefinition,
  type ConstructionSiteSnapshot,
  getAllConstructionDefinitions,
  getConstructionByHotkey,
  getConstructionsByCategory,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';

// ============================================================================
// Construction State Management
// ============================================================================
// Beautiful, scalable construction state for the client
// - Tracks all construction sites
// - Manages construction panel visibility
// - Handles hotkey selection
// ============================================================================

// Active construction sites (keyed by networkId)
const constructionSitesMap = signal<Map<number, ConstructionSiteSnapshot>>(new Map());

// Selected construction site for viewing details
export const selectedConstructionSite = signal<ConstructionSiteSnapshot | null>(null);

// UI state
export const isConstructionPanelOpen = signal<boolean>(false);
export const selectedConstruction = signal<ConstructionDefinition | null>(null);
export const activeCategory = signal<ConstructionCategoryValue>(ConstructionCategory.STRUCTURE);

// Placement mode state
export const isPlacingConstruction = signal<boolean>(false);
export const placementPreviewX = signal<number>(0);
export const placementPreviewY = signal<number>(0);
export const placementValid = signal<boolean>(true);

// Line placement state (for dragging to place multiple walls)
export const isLinePlacing = signal<boolean>(false);
export const lineStartX = signal<number>(0);
export const lineStartY = signal<number>(0);
export const lineTiles = signal<ReadonlyArray<{ x: number; y: number; valid: boolean }>>([]);

// ============================================================================
// Construction Site Actions
// ============================================================================

export const setConstructionSite = ({ site }: { site: ConstructionSiteSnapshot }): void => {
  const next = new Map(constructionSitesMap.value);
  next.set(site.networkId, site);
  constructionSitesMap.value = next;
};

export const setConstructionSites = ({
  sites,
}: {
  sites: ReadonlyArray<ConstructionSiteSnapshot>;
}): void => {
  const next = new Map(constructionSitesMap.value);
  for (const site of sites) {
    next.set(site.networkId, site);
  }
  constructionSitesMap.value = next;
};

export const updateConstructionSite = ({
  networkId,
  progress,
  state,
  workersAssigned,
}: {
  networkId: number;
  progress: number;
  state: number;
  workersAssigned: number;
}): void => {
  const site = constructionSitesMap.value.get(networkId);
  if (!site) {
    return;
  }

  const updatedSite = {
    ...site,
    progress,
    state,
    workersAssigned,
  };

  const next = new Map(constructionSitesMap.value);
  next.set(networkId, updatedSite);
  constructionSitesMap.value = next;

  // Update selected site if this is the one selected
  if (selectedConstructionSite.value?.networkId === networkId) {
    selectedConstructionSite.value = updatedSite;
  }
};

export const removeConstructionSite = ({ networkId }: { networkId: number }): void => {
  const next = new Map(constructionSitesMap.value);
  next.delete(networkId);
  constructionSitesMap.value = next;

  // Clear selection if this was the selected site
  if (selectedConstructionSite.value?.networkId === networkId) {
    selectedConstructionSite.value = null;
  }
};

export const getConstructionSite = ({
  networkId,
}: {
  networkId: number;
}): ConstructionSiteSnapshot | undefined => {
  return constructionSitesMap.value.get(networkId);
};

export const getAllConstructionSites = (): ReadonlyArray<ConstructionSiteSnapshot> => {
  return Array.from(constructionSitesMap.value.values());
};

export const clearConstructionSites = (): void => {
  constructionSitesMap.value = new Map();
  selectedConstructionSite.value = null;
};

// ============================================================================
// Construction Site Selection
// ============================================================================

export const selectConstructionSite = ({ networkId }: { networkId: number }): void => {
  const site = constructionSitesMap.value.get(networkId);
  selectedConstructionSite.value = site ?? null;
};

export const deselectConstructionSite = (): void => {
  selectedConstructionSite.value = null;
};

export const isConstructionSiteSelected = ({ networkId }: { networkId: number }): boolean => {
  return selectedConstructionSite.value?.networkId === networkId;
};

// ============================================================================
// UI Actions
// ============================================================================

export const toggleConstructionPanel = (): void => {
  isConstructionPanelOpen.value = !isConstructionPanelOpen.value;

  // Cancel placement mode when closing panel
  if (!isConstructionPanelOpen.value) {
    cancelPlacement();
  }
};

export const openConstructionPanel = (): void => {
  isConstructionPanelOpen.value = true;
};

export const closeConstructionPanel = (): void => {
  isConstructionPanelOpen.value = false;
  cancelPlacement();
};

export const setActiveCategory = ({ category }: { category: ConstructionCategoryValue }): void => {
  activeCategory.value = category;
};

export const selectConstruction = ({
  definition,
}: {
  definition: ConstructionDefinition | null;
}): void => {
  selectedConstruction.value = definition;

  // Enter placement mode if a construction is selected
  if (definition) {
    isPlacingConstruction.value = true;
    placementValid.value = true;
  } else {
    cancelPlacement();
  }
};

export const selectConstructionByHotkey = ({ hotkey }: { hotkey: string }): boolean => {
  const definition = getConstructionByHotkey({ hotkey });
  if (definition) {
    selectConstruction({ definition });
    openConstructionPanel();
    return true;
  }
  return false;
};

export const cancelPlacement = (): void => {
  isPlacingConstruction.value = false;
  selectedConstruction.value = null;
  isLinePlacing.value = false;
  lineTiles.value = [];
};

// ============================================================================
// Line Placement (for walls and similar)
// ============================================================================

export const startLinePlacement = ({ tileX, tileY }: { tileX: number; tileY: number }): void => {
  isLinePlacing.value = true;
  lineStartX.value = tileX;
  lineStartY.value = tileY;
  lineTiles.value = [{ x: tileX, y: tileY, valid: true }];
};

// Calculate tiles for horizontal line
const calculateHorizontalLine = ({
  startX,
  startY,
  endX,
  canPlaceAt,
}: {
  startX: number;
  startY: number;
  endX: number;
  canPlaceAt: ({ x, y }: { x: number; y: number }) => boolean;
}): Array<{ x: number; y: number; valid: boolean }> => {
  const tiles: Array<{ x: number; y: number; valid: boolean }> = [];
  const step = endX >= startX ? 1 : -1;
  for (let x = startX; step > 0 ? x <= endX : x >= endX; x += step) {
    tiles.push({ x, y: startY, valid: canPlaceAt({ x, y: startY }) });
  }
  return tiles;
};

// Calculate tiles for vertical line
const calculateVerticalLine = ({
  startX,
  startY,
  endY,
  canPlaceAt,
}: {
  startX: number;
  startY: number;
  endY: number;
  canPlaceAt: ({ x, y }: { x: number; y: number }) => boolean;
}): Array<{ x: number; y: number; valid: boolean }> => {
  const tiles: Array<{ x: number; y: number; valid: boolean }> = [];
  const step = endY >= startY ? 1 : -1;
  for (let y = startY; step > 0 ? y <= endY : y >= endY; y += step) {
    tiles.push({ x: startX, y, valid: canPlaceAt({ x: startX, y }) });
  }
  return tiles;
};

export const updateLinePlacement = ({
  tileX,
  tileY,
  canPlaceAt,
}: {
  tileX: number;
  tileY: number;
  canPlaceAt: ({ x, y }: { x: number; y: number }) => boolean;
}): void => {
  if (!isLinePlacing.value) {
    return;
  }

  const startX = lineStartX.value;
  const startY = lineStartY.value;
  const dx = tileX - startX;
  const dy = tileY - startY;

  const tiles =
    Math.abs(dx) >= Math.abs(dy)
      ? calculateHorizontalLine({ startX, startY, endX: tileX, canPlaceAt })
      : calculateVerticalLine({ startX, startY, endY: tileY, canPlaceAt });

  lineTiles.value = tiles;
};

export const endLinePlacement = (): ReadonlyArray<{ x: number; y: number }> => {
  const tiles = lineTiles.value.filter((t) => t.valid).map(({ x, y }) => ({ x, y }));
  isLinePlacing.value = false;
  lineTiles.value = [];
  return tiles;
};

export const updatePlacementPreview = ({
  tileX,
  tileY,
  isValid,
}: {
  tileX: number;
  tileY: number;
  isValid: boolean;
}): void => {
  placementPreviewX.value = tileX;
  placementPreviewY.value = tileY;
  placementValid.value = isValid;
};

// ============================================================================
// Computed Values
// ============================================================================

// All available constructions
export const allConstructions = computed((): ReadonlyArray<ConstructionDefinition> => {
  return getAllConstructionDefinitions();
});

// Constructions for the active category
export const categoryConstructions = computed((): ReadonlyArray<ConstructionDefinition> => {
  return getConstructionsByCategory({ category: activeCategory.value });
});

// All construction sites as array
export const constructionSites = computed((): ReadonlyArray<ConstructionSiteSnapshot> => {
  return Array.from(constructionSitesMap.value.values());
});

// Check if there are any active construction sites
export const hasActiveConstructions = computed((): boolean => {
  return constructionSitesMap.value.size > 0;
});

// Get construction site at a specific tile
export const getConstructionSiteAtTile = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): ConstructionSiteSnapshot | null => {
  for (const site of constructionSitesMap.value.values()) {
    // Check if tile is within the construction footprint
    if (
      tileX >= site.tileX &&
      tileX < site.tileX + site.width &&
      tileY >= site.tileY &&
      tileY < site.tileY + site.height
    ) {
      return site;
    }
  }
  return null;
};

// ============================================================================
// Hotkey Constants
// ============================================================================

// Main hotkey to open construction panel
export const CONSTRUCTION_PANEL_HOTKEY = 'B';

// Category hotkeys (when build menu is open)
// Note: 'S' removed to avoid conflict with other shortcuts
export const CATEGORY_HOTKEYS: Partial<Record<ConstructionCategoryValue, string>> = {
  [ConstructionCategory.FURNITURE]: 'F',
  [ConstructionCategory.WORKSTATION]: 'W',
  [ConstructionCategory.FLOOR]: 'L',
};
